import { defineComponent, nextTick, onMounted, ref, shallowRef, watch, type PropType } from 'vue';
import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { mediaQuery } from '../../scripts/lib/mediaQuery.js';

const templateElement = document.getElementById('image-view-thumbnails-template');

export default defineComponent({
  template: templateElement ? templateElement.innerHTML : '',

  props: {
    images: {
      type: Array as PropType<ProductImagesDTO[]>,
      required: true,
    },
    activeThumbnail: {
      type: Number as PropType<number | null>,
      required: false,
      default: null,
    },
  },

  setup(props) {
    const minimumSlidesForNavigation = ref(5);
    const swiper = ref<HTMLElement | null>(null);
    const swiperInstance = shallowRef<Swiper | null>(null);

    onMounted(() => {
      initializeSwiperSlider();
    });

    function initializeSwiperSlider() {
      let animationSpeedInMS = 300;
      if (mediaQuery.prefersReducedMotion()) {
        /* https://github.com/nolimits4web/Swiper/issues/1702 */
        animationSpeedInMS = 1;
      }
      swiperInstance.value = new Swiper(swiper.value as HTMLElement, {
        // NOSONAR
        modules: [Pagination],
        speed: animationSpeedInMS,
        enabled: props.images.length > 1,
        direction: 'vertical',
        slidesPerView: 1,
        slideToClickedSlide: false,
        spaceBetween: 6,
        loop: false,
        pagination: {
          el: '.ents-image-view__thumbnails--pagination',
          clickable: false,
          renderBullet: function (index: number, className: string) {
            return `<span class="${className}" data-index="${index}"></span>`;
          },
        },
      });
    }

    function reinitSwiperSlider() {
      if (swiperInstance.value) {
        swiperInstance.value.destroy();
        initializeSwiperSlider();
      }
    }

    watch(
      () => props.images,
      () => {
        nextTick(() => {
          reinitSwiperSlider();
        });
      },
    );

    watch(
      () => props.activeThumbnail,
      () => {
        nextTick(() => {
          if (props.activeThumbnail !== null) {
            swiperInstance.value?.slideTo(props.activeThumbnail);
          }
        });
      },
    );

    return {
      minimumSlidesForNavigation,
      swiper,
      swiperInstance,
    };
  },
});
